import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Shield, 
  ShieldCheck, 
  ShieldOff, 
  Smartphone, 
  QrCode,
  Loader2,
  Copy,
  CheckCircle,
  AlertTriangle,
  ArrowLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TwoFactorSettings = () => {
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [disableCode, setDisableCode] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const initiate2FASetup = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/2fa/setup`, {}, { headers });
      setSetupData(response.data);
      setShowSetupDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'initialisation du 2FA');
    } finally {
      setLoading(false);
    }
  };

  const verify2FASetup = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Veuillez entrer un code à 6 chiffres');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/2fa/verify`, 
        { code: verificationCode },
        { headers }
      );
      toast.success('Authentification à deux facteurs activée !');
      setShowSetupDialog(false);
      setSetupData(null);
      setVerificationCode('');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code invalide');
    } finally {
      setLoading(false);
    }
  };

  const disable2FA = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error('Veuillez entrer votre code 2FA actuel');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/2fa/disable`, 
        { code: disableCode },
        { headers }
      );
      toast.success('Authentification à deux facteurs désactivée');
      setShowDisableDialog(false);
      setDisableCode('');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code invalide');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    if (setupData?.secret) {
      navigator.clipboard.writeText(setupData.secret);
      toast.success('Code secret copié !');
    }
  };

  const is2FAEnabled = user?.two_factor_enabled;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="2fa-settings-page">
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour au tableau de bord
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary" />
            Authentification à deux facteurs
          </h1>
          <p className="text-slate-500 mt-1">
            Renforcez la sécurité de votre compte
          </p>
        </div>

        {/* Status Card */}
        <Card className={`mb-6 ${is2FAEnabled ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {is2FAEnabled ? (
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                    <ShieldCheck className="w-7 h-7 text-green-600" />
                  </div>
                ) : (
                  <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
                    <ShieldOff className="w-7 h-7 text-amber-600" />
                  </div>
                )}
                <div>
                  <p className={`font-semibold text-lg ${is2FAEnabled ? 'text-green-800' : 'text-amber-800'}`}>
                    {is2FAEnabled ? '2FA Activé' : '2FA Désactivé'}
                  </p>
                  <p className={`text-sm ${is2FAEnabled ? 'text-green-600' : 'text-amber-600'}`}>
                    {is2FAEnabled 
                      ? 'Votre compte est protégé par une double authentification'
                      : 'Activez le 2FA pour sécuriser votre compte'
                    }
                  </p>
                </div>
              </div>
              <Badge className={is2FAEnabled ? 'bg-green-500' : 'bg-amber-500'}>
                {is2FAEnabled ? 'Actif' : 'Inactif'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Main Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-primary" />
              Configuration
            </CardTitle>
            <CardDescription>
              Utilisez une application d'authentification comme Google Authenticator, Authy ou Microsoft Authenticator
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* How it works */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="font-medium text-slate-800 mb-3">Comment ça fonctionne ?</h4>
              <ol className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                  <span>Téléchargez une application d'authentification sur votre téléphone</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                  <span>Scannez le QR code ou entrez le code secret manuellement</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                  <span>Entrez le code à 6 chiffres généré par l'application pour confirmer</span>
                </li>
              </ol>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              {is2FAEnabled ? (
                <Button 
                  variant="destructive" 
                  onClick={() => setShowDisableDialog(true)}
                  className="gap-2"
                  data-testid="disable-2fa-btn"
                >
                  <ShieldOff className="w-4 h-4" />
                  Désactiver le 2FA
                </Button>
              ) : (
                <Button 
                  onClick={initiate2FASetup}
                  disabled={loading}
                  className="gap-2 bg-green-600 hover:bg-green-700"
                  data-testid="enable-2fa-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ShieldCheck className="w-4 h-4" />
                  )}
                  Activer le 2FA
                </Button>
              )}
            </div>

            {/* Warning */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">Important</p>
                  <p className="text-amber-700">
                    Si vous perdez l'accès à votre application d'authentification, vous devrez contacter le support pour récupérer l'accès à votre compte.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Setup Dialog */}
        <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <QrCode className="w-5 h-5 text-primary" />
                Configuration du 2FA
              </DialogTitle>
              <DialogDescription>
                Scannez ce QR code avec votre application d'authentification
              </DialogDescription>
            </DialogHeader>

            <div className="py-4 space-y-4">
              {/* QR Code */}
              {setupData?.qr_code && (
                <div className="flex justify-center">
                  <div className="bg-white p-4 rounded-lg border">
                    <img 
                      src={setupData.qr_code} 
                      alt="QR Code 2FA" 
                      className="w-48 h-48"
                    />
                  </div>
                </div>
              )}

              {/* Manual Secret */}
              {setupData?.secret && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 mb-2">Ou entrez ce code manuellement :</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded border text-sm font-mono break-all">
                      {setupData.secret}
                    </code>
                    <Button variant="outline" size="sm" onClick={copySecret}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Verification */}
              <div className="space-y-2">
                <Label htmlFor="verification-code">Code de vérification</Label>
                <Input
                  id="verification-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-2xl tracking-widest font-mono"
                  data-testid="2fa-verification-input"
                />
                <p className="text-xs text-slate-500">
                  Entrez le code à 6 chiffres affiché dans votre application
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSetupDialog(false)}>
                Annuler
              </Button>
              <Button 
                onClick={verify2FASetup}
                disabled={loading || verificationCode.length !== 6}
                className="gap-2"
                data-testid="verify-2fa-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Vérifier et activer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Disable Dialog */}
        <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <ShieldOff className="w-5 h-5" />
                Désactiver le 2FA
              </DialogTitle>
              <DialogDescription>
                Êtes-vous sûr de vouloir désactiver l'authentification à deux facteurs ?
              </DialogDescription>
            </DialogHeader>

            <div className="py-4 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">
                  <strong>Attention :</strong> Votre compte sera moins sécurisé sans le 2FA. 
                  Nous vous recommandons de le garder activé.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="disable-code">Code 2FA actuel</Label>
                <Input
                  id="disable-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-2xl tracking-widest font-mono"
                  data-testid="2fa-disable-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDisableDialog(false)}>
                Annuler
              </Button>
              <Button 
                variant="destructive"
                onClick={disable2FA}
                disabled={loading || disableCode.length !== 6}
                data-testid="confirm-disable-2fa-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Désactiver'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default TwoFactorSettings;
